// ═══════════════════════════════════════════════════════════════
// lmsUi — primitives UI premium partagées du module Formations
// ═══════════════════════════════════════════════════════════════
// Modal overlay, champs de formulaire, boutons, styles. Aligné sur
// le langage visuel ITRIIA (variables CSS --card-bg / --card-border /
// --text / --text-secondary / --text-muted, radius 12, accent bleu).
// ═══════════════════════════════════════════════════════════════
import React, { useEffect } from 'react';
import { X, Loader } from 'lucide-react';

export const ACCENT = '#8b5cf6';

// ─── Styles réutilisables ────────────────────────────────────────
export const s = {
    label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 },
    input: {
        width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 9,
        border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)',
        fontSize: 14, outline: 'none', fontFamily: 'inherit',
    },
    hint: { fontSize: 11, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.4 },
    card: { border: '1px solid var(--card-border)', borderRadius: 12, background: 'var(--card-bg)', padding: 16 },
    primaryBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '9px 16px', borderRadius: 9, background: ACCENT, color: '#fff', border: 'none',
        cursor: 'pointer', fontWeight: 600, fontSize: 13,
    },
    ghostBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '9px 14px', borderRadius: 9, background: 'var(--card-bg)', color: 'var(--text)',
        border: '1px solid var(--card-border)', cursor: 'pointer', fontWeight: 500, fontSize: 13,
    },
    iconBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 8, border: '1px solid var(--card-border)',
        background: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer',
    },
    chip: (active) => ({
        padding: '6px 12px', borderRadius: 20, fontSize: 12.5, cursor: 'pointer',
        border: `1px solid ${active ? ACCENT : 'var(--card-border)'}`,
        background: active ? 'rgba(139,92,246,0.12)' : 'var(--card-bg)',
        color: active ? ACCENT : 'var(--text-secondary)', fontWeight: active ? 600 : 500,
    }),
};

export const Spinner = ({ size = 14 }) => <Loader size={size} className="expertise-spin" />;

// ─── Champ étiqueté ──────────────────────────────────────────────
export function Field({ label, hint, children, style }) {
    return (
        <div style={{ marginBottom: 16, ...style }}>
            {label && <label style={s.label}>{label}</label>}
            {children}
            {hint && <div style={s.hint}>{hint}</div>}
        </div>
    );
}

export const TextInput = (props) => <input {...props} style={{ ...s.input, ...(props.style || {}) }} />;
export const TextArea = (props) => (
    <textarea {...props} style={{ ...s.input, resize: 'vertical', minHeight: 90, lineHeight: 1.5, ...(props.style || {}) }} />
);
export const Select = ({ children, ...props }) => (
    <select {...props} style={{ ...s.input, cursor: 'pointer', ...(props.style || {}) }}>{children}</select>
);

// ─── Modal overlay premium ───────────────────────────────────────
export function Modal({ title, subtitle, icon: Icon, onClose, children, footer, width = 560, busy = false }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, busy]);

    return (
        <div
            onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(10,12,20,0.55)',
                backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20,
            }}
        >
            <div style={{
                width: '100%', maxWidth: width, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
                background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 16,
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden',
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px',
                    borderBottom: '1px solid var(--card-border)',
                }}>
                    {Icon && (
                        <div style={{
                            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                            background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Icon size={18} style={{ color: ACCENT }} />
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
                        {subtitle && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{subtitle}</div>}
                    </div>
                    <button onClick={() => !busy && onClose?.()} style={s.iconBtn} aria-label="Fermer"><X size={16} /></button>
                </div>

                <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>{children}</div>

                {footer && (
                    <div style={{
                        display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px',
                        borderTop: '1px solid var(--card-border)',
                    }}>{footer}</div>
                )}
            </div>
        </div>
    );
}

// Slugify partagé (titre → slug url)
export const slugify = (str) =>
    (str || '').toLowerCase().normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
